import { DayjsProperty } from '@/lib/jackson'
import dayjs, { Dayjs } from 'dayjs'
import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable } from '../model'

/**
 * Stable seeded FacilityType id for "Sports field / Court".
 * Same value across dev and prod seeds (fitup-backend prisma/.../facilitity-types.ts).
 * Used to gate sport-court-only config (split court, etc.) in the UI.
 * NOTE: if admins later create additional sport-court types, replace this id check
 * with a `category` flag on FacilityType (see facility-booking-fe-checklist §1).
 */
export const SPORTS_FIELD_FACILITY_TYPE_ID = '062e97d9-8a75-4752-ab6e-4443b00e10ae'

/**
 * Stable seeded FacilityType id for "Swimming Pool" (same across dev/prod seeds).
 * Used to show lane-count config for pools.
 */
export const SWIMMING_POOL_FACILITY_TYPE_ID = '019b9969-0284-75c6-b297-197bcb0a48f8'

/** Sport options for a "Sports field / Court" facility (FE list until backend provides one). */
export const FACILITY_SPORTS = [
  'tennis',
  'badminton',
  'futsal',
  'football',
  'basketball',
  'volleyball',
  'table-tennis',
  'swimming',
  'other',
] as const

export interface FacilitySummary {
  revenue: number
  forfeitedDeposits: number
  totalBookings: number
  noShow: number
  cancelled: number
  bookedHours: number
  occupancyRate: number
  hasOpenHours: boolean
  memberBookings: number
  dropInBookings: number
  totalPlayers: number
  lostHours: number
  lostRevenue: number
  bySport: {
    sport: string
    revenue: number
    bookings: number
    occupancyRate: number
  }[]
  heatmap: { days: string[]; hours: number[]; data: number[][] }
  byCourt: { name: string; occupancyRate: number }[]
  topBookers: { name: string; count: number }[]
  gender: { male: number; female: number; other: number }
  ageBuckets: {
    under18: number
    age18_30: number
    age31_45: number
    age46plus: number
  }
}

export interface PaymentInfo {
  paymentMethod: 'FREE' | 'CREDIT' | 'PAID' | 'PACKAGE' | 'NOT_ALLOW'
  creditAmount?: number
  priceAmount?: number
}

export interface FacilityPricings {
  facilityId: string
  id: string
  participantType: string
  paymentMethod: string
  creditAmount?: number
  priceAmount?: number
  /** Sport this pricing row applies to. null/absent = default set (applies to all sports). */
  sport?: string | null
}

/**
 * A peak/off-peak pricing rule as returned on the facility detail/list response. Carried so the
 * edit form can rehydrate its rules instead of wiping them on save. `timeStart`/`timeEnd` are the
 * hour boundaries (0–24); `sport` null/absent = the default set that applies to all sports.
 */
export interface FacilityPricingRuleData {
  participantType: string
  dayType: string
  timeStart: number
  timeEnd: number
  price: number
  sport?: string | null
}

/** Base pricing (member/session/drop-in) for one specific sport — a per-sport override set. */
export interface SportPricingOverride {
  sport: string
  memberPayment: PaymentInfo
  sessionMemberPayment: PaymentInfo
  dropInPayment: PaymentInfo
}

export type FacilityAllowedGender = 'MALE' | 'FEMALE' | 'NOT_SPECIFIED'

export interface CreateFacilityPayload {
  id?: string
  name: string
  branchId: number
  typeId: string
  capacity: number
  status: 'ACTIVE' | 'INACTIVE' | 'UNDER_MAINTENANCE'
  instructorRequired: boolean
  description: string
  bookingLeadTimeMinutes: number
  bookingCancelTimeMinutes: number
  minimumUsageTimeMinutes: number
  maximumUsageTimeMinutes: number
  monIsClosed: boolean
  monOpenTime: string
  monCloseTime: string
  tueIsClosed: boolean
  tueOpenTime: string
  tueCloseTime: string
  wedIsClosed: boolean
  wedOpenTime: string
  wedCloseTime: string
  thuIsClosed: boolean
  thuOpenTime: string
  thuCloseTime: string
  friIsClosed: boolean
  friOpenTime: string
  friCloseTime: string
  satIsClosed: boolean
  satOpenTime: string
  satCloseTime: string
  sunIsClosed: boolean
  sunOpenTime: string
  sunCloseTime: string
  memberPayment: PaymentInfo
  sessionMemberPayment: PaymentInfo
  dropInPayment: PaymentInfo
  imageUrl?: string[]
  minAgeYears?: number | null
  maxAgeYears?: number | null
  allowedGenders?: FacilityAllowedGender[]
  /** Legacy single sport — kept in sync server-side as sports[0]. */
  sport?: string
  /** Multi-sport support. The BE keeps `sport = sports[0] ?? null` for old clients/reports. */
  sports?: string[]
  /**
   * Composite parent-child courts: this facility is a sub-court of `parentFacilityId`
   * (null/absent = standalone). Booking the parent blocks all children and vice versa.
   */
  parentFacilityId?: string | null
  serviceId?: number
  depositRequired?: boolean
  depositAmount?: number
  /** Walk-in access (QTY-1989): expose in the LINE OA walk-in list. Defaults false. */
  isWalkIn?: boolean
  /**
   * Peak / off-peak rules. `sport` (null/absent = default set) tags the sport-specific set the
   * rule belongs to; a sport-specific set fully replaces the default set for that participant type.
   */
  pricingRules?: {
    participantType: string
    dayType: string
    timeStart: number
    timeEnd: number
    price: number
    sport?: string | null
  }[]
  /**
   * Per-sport base-pricing overrides. Each entry expands server-side into member/session/drop-in
   * FacilityPricing rows tagged with its `sport`. Sports without an entry fall back to the default
   * set (memberPayment/sessionMemberPayment/dropInPayment above).
   */
  sportPricings?: SportPricingOverride[]
}

export interface CreateFacilityBookingPayload {
  facilityId: string
  /** Member user id. Omit for a walk-in/guest (send guestName instead). */
  userId?: number
  guestName?: string
  guestPhone?: string
  startAt: string
  endAt: string
  instructorUserId?: number
  slipImageUrl?: string
  /**
   * Staff discount on the booking's WHOLE cash bill — court cash + rental cash. PERCENT = % of
   * that bill (0–100), AMOUNT = flat baht capped at it, so a quota/credit/free court whose only
   * cash is rental can still be discounted and a flat amount can bite into the rental. The BE
   * stores totalPrice NET. On a recurring series (which carries no rental) the discount applies
   * to every occurrence's court cash.
   */
  discountType?: 'AMOUNT' | 'PERCENT'
  discountValue?: number
  userMembershipId?: number
  partySize?: number
  /**
   * Sport being played. Required when the facility supports >1 sport (must be one of
   * facility.sports); optional otherwise (BE defaults to facility.sports[0]).
   */
  sport?: string
  /**
   * Equipment rental line items for this booking. SINGLE bookings only — recurring does not
   * accept rentals in v1. The server snapshots each item's name + price and computes totals.
   * Rental is always cash regardless of how the court itself is paid (quota/credit/free).
   */
  rentalItems?: { rentalItemId: string; quantity: number }[]
}

/**
 * Weekly recurring series (regular group / ก๊วนประจำ): the base booking repeats at the same time
 * on the selected weekdays, every week from startAt through endDate inclusive.
 * POST /facility-bookings/recurring.
 */
export interface CreateRecurringFacilityBookingPayload extends CreateFacilityBookingPayload {
  /** Last calendar day of the series (inclusive), ISO string. */
  endDate: string
  /**
   * Weekdays to book in every week (0=Sunday…6=Saturday), e.g. [1,3,5] = Mon/Wed/Fri.
   * Defaults to startAt's weekday.
   */
  daysOfWeek?: number[]
  /** true = create the free slots and report conflicts as skipped; false = reject on any conflict. */
  skipConflicts?: boolean
}

/** Per-occurrence availability from POST /facility-bookings/recurring/preview. */
export interface RecurringBookingPreviewSlot {
  startAt: string
  endAt: string
  available: boolean
}

/** Result of POST /facility-bookings/recurring. */
export interface RecurringBookingResult {
  recurringGroupId: string
  created: Record<string, unknown>[]
  skipped: { startAt: string; endAt: string; reason: string }[]
}

/** Slot from GET /facilities/:id/availability (FacilitiesController_getAvailability). */
export interface FacilityAvailabilitySlot {
  startTime: string
  endTime: string
  availableCount?: number
}

@Serializable()
export class FacilityDataList extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('type')
  type!: string

  @JsonProperty('capacity')
  capacity!: number

  @JsonProperty('status')
  status!: string

  /** Legacy single sport (= sports[0]); kept for old callers. Prefer `sports`. */
  @JsonProperty('sport')
  sport?: string

  /** Sports this court supports (multi-sport). Empty for non-sport facilities. */
  @JsonProperty('sports')
  sports?: string[]

  /** Composite parent court id — this facility is a sub-court of it (null = standalone). */
  @JsonProperty('parentFacilityId')
  parentFacilityId?: string | null

  /** Walk-in access (QTY-1989) — shown as an indicator in the admin facility list. */
  @JsonProperty('isWalkIn')
  isWalkIn?: boolean

  @JsonProperty('description')
  description!: string

  @JsonProperty('pendingPayment')
  pendingPayment!: string

  @JsonProperty('facilityPricings')
  facilityPricings?: FacilityPricings[]

  /** Peak/off-peak rules (default + per-sport). Optional/defensive — tolerate BE not sending it. */
  @JsonProperty('facilityPricingRules')
  facilityPricingRules?: FacilityPricingRuleData[]

  /** Facility type relation — needed so Reschedule can list other courts of the same type. */
  @JsonProperty('facilityType')
  facilityType?: FacilityType

  public DataForTable() {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      capacity: this.capacity,
      status: this.status,
      isWalkIn: this.isWalkIn ?? false,
      description: this.description,
      pendingPayment: this.pendingPayment,
    }
  }
}

export interface FacilityType {
  active: boolean
  id: string
  name: string
}

@Serializable()
export class FacilityData extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('branchId')
  branchId!: number

  @JsonProperty('type')
  type!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('description')
  description!: string

  @JsonProperty('facilityType')
  facilityType?: FacilityType

  @JsonProperty('capacity')
  capacity!: number

  /** Legacy single sport (= sports[0]); kept for old callers. Prefer `sports`. */
  @JsonProperty('sport')
  sport?: string

  /** Sports this court supports (multi-sport). Empty for non-sport facilities. */
  @JsonProperty('sports')
  sports?: string[]

  /** Composite parent court id — this facility is a sub-court of it (null = standalone). */
  @JsonProperty('parentFacilityId')
  parentFacilityId?: string | null

  @JsonProperty('imageUrl')
  imageUrl!: string[]

  @JsonProperty('status')
  status!: 'ACTIVE' | 'INACTIVE' | 'UNDER_MAINTENANCE'

  @JsonProperty('instructorRequired')
  instructorRequired!: boolean

  @JsonProperty('bookingLeadTimeMinutes')
  bookingLeadTimeMinutes!: number

  @JsonProperty('bookingCancelTimeMinutes')
  bookingCancelTimeMinutes!: number

  @JsonProperty('minimumUsageTimeMinutes')
  minimumUsageTimeMinutes!: number

  @JsonProperty('maximumUsageTimeMinutes')
  maximumUsageTimeMinutes!: number

  @JsonProperty('monIsClosed')
  monIsClosed!: boolean

  @JsonProperty('monOpenTime')
  monOpenTime!: string

  @JsonProperty('monCloseTime')
  monCloseTime!: string

  @JsonProperty('tueIsClosed')
  tueIsClosed!: boolean

  @JsonProperty('tueOpenTime')
  tueOpenTime!: string

  @JsonProperty('tueCloseTime')
  tueCloseTime!: string

  @JsonProperty('wedIsClosed')
  wedIsClosed!: boolean

  @JsonProperty('wedOpenTime')
  wedOpenTime!: string

  @JsonProperty('wedCloseTime')
  wedCloseTime!: string

  @JsonProperty('thuIsClosed')
  thuIsClosed!: boolean

  @JsonProperty('thuOpenTime')
  thuOpenTime!: string

  @JsonProperty('thuCloseTime')
  thuCloseTime!: string

  @JsonProperty('friIsClosed')
  friIsClosed!: boolean

  @JsonProperty('friOpenTime')
  friOpenTime!: string

  @JsonProperty('friCloseTime')
  friCloseTime!: string

  @JsonProperty('satIsClosed')
  satIsClosed!: boolean

  @JsonProperty('satOpenTime')
  satOpenTime!: string

  @JsonProperty('satCloseTime')
  satCloseTime!: string

  @JsonProperty('sunIsClosed')
  sunIsClosed!: boolean

  @JsonProperty('sunOpenTime')
  sunOpenTime!: string

  @JsonProperty('sunCloseTime')
  sunCloseTime!: string

  @JsonProperty('minAgeYears')
  minAgeYears?: number | null

  @JsonProperty('maxAgeYears')
  maxAgeYears?: number | null

  @JsonProperty('allowedGenders')
  allowedGenders?: FacilityAllowedGender[]

  /** Deposit config — restored into the edit form so saving doesn't wipe it (BE returns both). */
  @JsonProperty('depositRequired')
  depositRequired?: boolean

  @JsonProperty('depositAmount')
  depositAmount?: number | null

  /** Walk-in access (QTY-1989): exposed in the LINE OA walk-in list, bookable by drop-in members. */
  @JsonProperty('isWalkIn')
  isWalkIn?: boolean

  @DayjsProperty()
  createdAt!: Dayjs

  @DayjsProperty()
  updatedAt!: Dayjs

  @DayjsProperty()
  deletedAt?: Dayjs

  @JsonProperty('occupiedCount')
  occupiedCount!: number

  /**
   * QTY-1995: bookings that are active or upcoming (not cancelled/no-show, endedAt >= now).
   * Drives the edit-form field locks; the BE re-validates at save time.
   */
  @JsonProperty('activeUpcomingBookingCount')
  activeUpcomingBookingCount?: number

  @JsonProperty('facilityPricings')
  facilityPricings?: FacilityPricings[]

  /**
   * Peak/off-peak rules (default set + per-sport sets). Optional/defensive so an older BE that
   * omits them doesn't break; the edit form maps them back into its rules state to avoid wiping.
   */
  @JsonProperty('facilityPricingRules')
  facilityPricingRules?: FacilityPricingRuleData[]
}

@Serializable()
export class FacilityBookedListUser extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('code')
  code!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('gender')
  gender!: string
}

@Serializable()
export class FacilityBookedListInstructor extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('code')
  code!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('gender')
  gender!: string
}

/** One equipment-rental line on a booking (price/name snapshotted at booking time). */
@Serializable()
export class FacilityBookingRentalItem extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('rentalItemId')
  rentalItemId!: string

  /** Catalog item name at booking time. */
  @JsonProperty('name')
  name!: string

  /** Snapshotted unit price (baht). */
  @JsonProperty('unitPrice')
  unitPrice!: number

  @JsonProperty('quantity')
  quantity!: number

  /** unitPrice × quantity (baht). */
  @JsonProperty('totalPrice')
  totalPrice!: number
}

@Serializable()
export class FacilityBookedListItem extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('branchId')
  branchId!: number

  @JsonProperty('facility')
  facility!: FacilityDataList

  @DayjsProperty()
  startedAt!: Dayjs

  @DayjsProperty()
  endedAt!: Dayjs

  @JsonProperty('status')
  status!: string

  @JsonProperty('createdByUser')
  createdByUser?: FacilityBookedListUser

  @JsonProperty('guestName')
  guestName?: string | null

  @JsonProperty('guestPhone')
  guestPhone?: string | null

  @JsonProperty('cancelledByUser')
  cancelledByUser?: FacilityBookedListUser

  @DayjsProperty()
  cancelledAt?: Dayjs

  @JsonProperty('instructor')
  instructor?: FacilityBookedListInstructor

  @DayjsProperty()
  createdAt!: Dayjs

  @DayjsProperty()
  updatedAt!: Dayjs

  @JsonProperty('facilityId')
  facilityId!: string

  /** Sport played for this booking (null = facility had ≤1 sport / legacy row). */
  @JsonProperty('sport')
  sport?: string | null

  @JsonProperty('durationMinutes')
  durationMinutes!: number

  @JsonProperty('partySize')
  partySize?: number | null

  /**
   * Wellness & Spa: a spa booking IS a facility booking, and the member's own history is the one
   * place they are deliberately mixed back in (the court board / list / report still exclude
   * them). Present only on member-history rows.
   */
  @JsonProperty('isSpa')
  isSpa?: boolean

  /** Treatment name for a spa row — the room name alone says nothing about what was done. */
  @JsonProperty('spaServiceName')
  spaServiceName?: string | null

  @JsonProperty('paymentStatus')
  paymentStatus!: string

  /**
   * Transient create-response flag: the member holds a covering package but the quota deduction
   * failed (ran out / not covering), so the booking was charged its cash/credit price instead.
   * Only present on the create response — list/detail fetches never carry it.
   */
  @JsonProperty('packageFallbackToCash')
  packageFallbackToCash?: boolean

  @JsonProperty('paymentSlipUrl')
  paymentSlipUrl?: string

  /** Settlement channel: CASH / CARD (counter) or TRANSFER (slip). Null = credit/package/free. */
  @JsonProperty('paidWith')
  paidWith?: 'CASH' | 'CARD' | 'TRANSFER' | null

  @JsonProperty('totalPrice')
  totalPrice!: number

  @JsonProperty('totalCredits')
  totalCredits!: number

  /** Staff discount granted at booking time (court cash only). totalPrice is already NET. */
  @JsonProperty('discountType')
  discountType?: 'AMOUNT' | 'PERCENT' | null

  @JsonProperty('discountValue')
  discountValue?: number | null

  /** Resolved baht deducted from the court cash (0 = no discount). */
  @JsonProperty('discountAmount')
  discountAmount?: number

  @JsonProperty('depositAmount')
  depositAmount?: number | null

  @JsonProperty('balanceAmount')
  balanceAmount?: number | null

  @JsonProperty('depositStatus')
  depositStatus?: string

  @JsonProperty('checkInAt')
  checkInAt?: string | null

  /** Weekly recurring series id — set on every occurrence created together (null = one-off). */
  @JsonProperty('recurringGroupId')
  recurringGroupId?: string | null

  @JsonProperty('userMembershipId')
  userMembershipId?: string

  @JsonProperty('createdByUserId')
  createdByUserId?: number | null

  @JsonProperty('cancelledByUserId')
  cancelledByUserId?: number

  @JsonProperty('paymentType')
  paymentType?: string

  /** Equipment rental line items on this booking (always cash). */
  @JsonProperty({ path: 'rentalItems', elementType: FacilityBookingRentalItem })
  rentalItems?: FacilityBookingRentalItem[]

  /** Sum of rental line totals (baht). Court price + this = totalPrice cash due. */
  @JsonProperty('rentalTotalPrice')
  rentalTotalPrice?: number

  public DataForTable() {
    return {
      id: this.id,
      date: this?.startedAt,
      duration: {
        durationTime:
          this?.startedAt && this?.endedAt
            ? `${dayjs(this?.startedAt).format('HH:mm')} - ${dayjs(this?.endedAt).format('HH:mm')}`
            : '-',
        durationMinutes: this?.durationMinutes,
      },
      status: this.status,
      reversedName:
        this?.createdByUser?.firstName && this?.createdByUser?.lastName
          ? this?.createdByUser?.firstName + ' ' + this?.createdByUser?.lastName
          : this?.guestName
            ? `${this.guestName} (Walk-in)`
            : '-',
      reversedById: this?.createdByUser?.id,
      instructorName:
        this?.instructor?.firstName || this?.instructor?.lastName
          ? this?.instructor?.firstName + ' ' + this?.instructor?.lastName
          : '-',
      instructorId: this?.instructor?.id,
      paymentStatus: this?.paymentStatus,
      depositStatus: this?.depositStatus,
      depositAmount: this?.depositAmount,
      totalPrice: this?.totalPrice,
      totalCredits: this?.totalCredits,
      checkInAt: this?.checkInAt,
    }
  }
}

export type FindFacilityBookingMembershipParams = {
  facilityId: string
  userId: number
  date?: string
}

@Serializable()
export class FacilityBookingMembershipQuotaFacility extends Fromable {
  @JsonProperty('facilityId')
  facilityId!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('id')
  id!: number

  @JsonProperty('packageQuotaId')
  packageQuotaId!: number
}

@Serializable()
export class FacilityBookingMembershipPackageQuota extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('limit')
  limit!: number

  @JsonProperty('packageId')
  packageId!: number

  @JsonProperty('quotaResetType')
  quotaResetType!: string

  /** SESSION = one use per booking; HOUR = one use per booked hour (part-hours round up). */
  @JsonProperty('usageUnit')
  usageUnit?: 'SESSION' | 'HOUR'

  @JsonProperty('isUnlimit')
  isUnlimit?: boolean

  @JsonProperty({ path: 'quotaFacilities', elementType: FacilityBookingMembershipQuotaFacility })
  quotaFacilities!: FacilityBookingMembershipQuotaFacility[]

  @JsonProperty('quotaClasses')
  quotaClasses!: unknown[]

  @JsonProperty('quotaServices')
  quotaServices!: unknown[]
}

@Serializable()
export class FacilityBookingMembership extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('packageQuotaId')
  packageQuotaId!: number

  @JsonProperty('remainingUses')
  remainingUses!: number

  @JsonProperty('isUnlimit')
  isUnlimit?: boolean

  @JsonProperty({ path: 'packageQuota', type: FacilityBookingMembershipPackageQuota })
  packageQuota!: FacilityBookingMembershipPackageQuota

  @JsonProperty('userMembershipId')
  userMembershipId!: number

  @JsonProperty('quotaResetType')
  quotaResetType!: string
}

/* -------------------------------------------------------------------------- */
/* Blocked slots (section C): staff close a court for a time range.            */
/* -------------------------------------------------------------------------- */

export interface CreateFacilityBlockedSlotPayload {
  facilityId: string
  startedAt: string
  endedAt: string
  reason?: string
}

/**
 * An active booking overlapping a new block's range — staff must resolve these manually.
 * `facilityId` names the family court the conflict sits on (a block also blocks composite
 * parent/children); exactly one of memberName/guestName is set.
 */
export interface FacilityBlockedSlotConflict {
  id: string
  bookingNo: number
  facilityId: string
  startedAt: string
  endedAt: string
  memberName?: string | null
  guestName?: string | null
}

@Serializable()
export class FacilityBlockedSlot extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('branchId')
  branchId!: number

  @JsonProperty('facilityId')
  facilityId!: string

  /** Court name, denormalized by the BE so the board/detail need no lookup. */
  @JsonProperty('facilityName')
  facilityName?: string

  @DayjsProperty()
  startedAt!: Dayjs

  @DayjsProperty()
  endedAt!: Dayjs

  @JsonProperty('reason')
  reason?: string | null

  @JsonProperty('createdByUserId')
  createdByUserId?: number | null

  /** Staff who created the block ({id, code, firstName, lastName, nickname, gender, lineId}). */
  @JsonProperty({ path: 'createdByUser', type: FacilityBookedListUser })
  createdByUser?: FacilityBookedListUser

  @DayjsProperty()
  createdAt?: Dayjs

  @DayjsProperty()
  updatedAt?: Dayjs
}

/** POST /facility-blocked-slots response: the created block + any bookings it now overlaps. */
export interface CreateFacilityBlockedSlotResult {
  blockedSlot: FacilityBlockedSlot
  conflictingBookings: FacilityBlockedSlotConflict[]
}

/* -------------------------------------------------------------------------- */
/* Rental items (section D): per-branch catalog of rentable gear.              */
/* -------------------------------------------------------------------------- */

@Serializable()
export class FacilityRentalItem extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('name')
  name!: string

  /** Flat price per booking (baht) — not per hour. */
  @JsonProperty('price')
  price!: number

  @JsonProperty('active')
  active!: boolean

  @JsonProperty('imageUrl')
  imageUrl?: string | null

  @JsonProperty('sortOrder')
  sortOrder?: number
}

export interface CreateFacilityRentalItemPayload {
  name: string
  price: number
  imageUrl?: string
  sortOrder?: number
}

export interface UpdateFacilityRentalItemPayload {
  name?: string
  price?: number
  active?: boolean
  imageUrl?: string
  sortOrder?: number
}
